/**
 * errorReporter — silent fire-and-forget POST /api/errors capture for user-
 * visible failures (API toasts via the axios interceptor, ErrorBoundary
 * crashes). Designed to be called from already-failing code paths, so it
 * MUST NOT throw, and must not generate an infinite loop if its own POST
 * fails.
 *
 * Three-layer loop protection (per #331 edge cases):
 *   1. **Path skip** — requests against `/api/errors` itself are never
 *      reported. (`reportApiError` returns early.) Without this, a 500 on
 *      the report endpoint would re-enter the reporter through the axios
 *      interceptor and so on.
 *   2. **Throttle** — at most `MAX_PER_WINDOW` captures per
 *      `WINDOW_MS` rolling window; the 11th capture in <60s is silently
 *      dropped. Prevents storms when a render loop or polling timer keeps
 *      throwing.
 *   3. **Catch-all** — the POST is awaited inside a `.catch(() => {})`,
 *      so a failing capture never bubbles back into the caller (which is
 *      itself in an error path).
 *
 * Tests in `errorReporter.test.ts` cover all three layers.
 */

import axios from 'axios';
import type { ApiErrorDetail, BoundaryErrorDetail, ErrorReport } from './types';
import { UploadHttpError } from './uploadHttpError';

const ENDPOINT = '/api/errors';
const MAX_PER_WINDOW = 10;
const WINDOW_MS = 60_000;

const captureTimestamps: number[] = [];

function pageUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname + window.location.search;
}

function userAgent(): string {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent.slice(0, 300);
}

function allowedByThrottle(now: number = Date.now()): boolean {
  // Drop expired entries (older than the window).
  while (captureTimestamps.length > 0 && now - captureTimestamps[0] > WINDOW_MS) {
    captureTimestamps.shift();
  }
  if (captureTimestamps.length >= MAX_PER_WINDOW) return false;
  captureTimestamps.push(now);
  return true;
}

async function send(report: ErrorReport): Promise<void> {
  try {
    // Direct axios call (NOT the shared apiClient) so the reporter never goes
    // through the interceptor that would call us back. The endpoint is
    // same-origin, so no CORS concern.
    await axios.post(ENDPOINT, report, {
      headers: { 'Content-Type': 'application/json' },
      // Reasonable cap — if the endpoint hangs we still don't want this to
      // pin a request slot forever.
      timeout: 5_000,
    });
  } catch {
    // Layer 3 — silent. A failing POST /api/errors must NOT propagate or
    // trigger another capture.
  }
}

/**
 * Capture an API-layer error (axios interceptor calls this).
 *
 * The `requestUrl` is checked against ENDPOINT so a failed POST /api/errors
 * itself never triggers another capture (Layer 1 — path skip).
 */
export function reportApiError(detail: ApiErrorDetail, message: string): void {
  if (detail.requestUrl.endsWith(ENDPOINT) || detail.requestUrl.includes(`${ENDPOINT}/`)) {
    return;
  }
  if (!allowedByThrottle()) return;

  const report: ErrorReport = {
    source: 'api',
    message: message.slice(0, 500),
    pageUrl: pageUrl(),
    apiDetail: detail,
    userAgent: userAgent(),
  };
  // Fire-and-forget. `void` keeps lint quiet about the unawaited promise;
  // `.catch` inside send() is the Layer-3 catch.
  void send(report);
}

/**
 * Capture an ErrorBoundary crash (`componentDidCatch` calls this).
 */
export function reportBoundaryError(
  detail: BoundaryErrorDetail,
  message?: string,
): void {
  if (!allowedByThrottle()) return;

  const report: ErrorReport = {
    source: 'errorBoundary',
    message: (message ?? `${detail.errorName}: ${detail.errorMessage}`).slice(0, 500),
    pageUrl: pageUrl(),
    boundaryDetail: detail,
    userAgent: userAgent(),
  };
  void send(report);
}

/**
 * NC-1722: capture a failed browser → Blob PUT (SAS upload).
 *
 * Those requests go straight to the storage account and bypass the apiClient
 * interceptor, so without this call a 403 from the storage account never
 * reached Feedback-und-Fehler (live: Workshop-Kit.zip, 2026-10-30). Only an
 * `UploadHttpError` is reported: the other steps of the upload path
 * (upload-url, confirm) already travel through the interceptor, and a
 * user-cancelled upload is not an error. The URL inside the error is already
 * SAS-stripped (see uploadHttpError.ts), so no bearer credential is sent.
 */
export function reportUploadError(
  err: unknown,
  context: { kind: 'training-file' | 'installer'; fileName?: string },
): void {
  if (!(err instanceof UploadHttpError)) return;
  const what = context.fileName ? ` ${context.fileName}` : '';
  reportApiError(
    { status: err.status, method: 'PUT', requestUrl: err.requestUrl },
    `Blob-Upload (${context.kind})${what}: ${err.message}`,
  );
}

/**
 * Test-only — clear the throttle window between tests.
 * Not exported via the package barrel; only used by `errorReporter.test.ts`.
 */
export function __resetForTests(): void {
  captureTimestamps.length = 0;
}
